import React, { useEffect, useMemo, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import TelaInicial from "./TelaInicial";
import logoInvexa from "../assets/logo.jpg";
import { styles } from "../styles/appStyles";

import {
  apenasNumeros,
  decimalInputValido,
  formatarDataHora,
  formatarMoeda,
  paraNumero,
} from "../utils/formatters";

import {
  cadastrarUsuario,
  entrarUsuario,
  observarAuth,
  sairUsuario,
} from "../services/auth";

import {
  adicionarProduto,
  atualizarProduto,
  buscarConfiguracaoApp,
  excluirProduto,
  ouvirMovimentacoes,
  ouvirProdutos,
  ouvirReceitas,
  ouvirVendas,
  registrarMovimentacao,
  registrarVenda,
  salvarConfiguracaoApp,
  adicionarReceita,
  excluirReceita,
} from "../services/firestore";

import { inicializarBilling } from "../services/billing";

const VERSAO_APP = "invexa-food-v2.2.0";
const VALOR_MENSAL = 39.99;
const DIAS_GRATIS = 15;

const novoInsumoInicial = {
  nome: "",
  categoria: "",
  codigoBarras: "",
  estoque: "",
  limiteAlerta: "12",
  custo: "",
  precoVenda: "",
  unidade: "un",
};

const receitaInicial = {
  nome: "",
  precoVenda: "",
  ingredientes: [],
};

const itemIngredienteInicial = {
  insumoId: "",
  quantidade: "",
};

const configInicial = {
  nomeNegocio: "Invexa Food",
  alertaPadrao: 12,
  moeda: "BRL",
};

const filtrosIniciais = {
  busca: "",
  categoria: "todas",
  status: "todos",
  ordenacao: "nome",
};

function styleMerge(...items) {
  return Object.assign({}, ...items.filter(Boolean));
}

function Botao({ children, onClick, style, type = "button", disabled = false }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={styleMerge(style, {
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      })}
    >
      {children}
    </button>
  );
}

function Toasts({ toasts }) {
  return (
    <div style={styles.toastContainer}>
      {toasts.map((toast) => (
        <div
          key={toast.id}
          style={styleMerge(
            styles.toast,
            toast.tipo === "sucesso"
              ? styles.toastSucesso
              : toast.tipo === "erro"
              ? styles.toastErro
              : styles.toastInfo
          )}
        >
          {toast.mensagem}
        </div>
      ))}
    </div>
  );
}

function insumoEmAlerta(insumo) {
  return Number(insumo.estoque || 0) <= Number(insumo.limiteAlerta || 12);
}

function ordenarInsumos(lista, ordenacao) {
  const copia = [...lista];

  copia.sort((a, b) => {
    const alertaA = insumoEmAlerta(a);
    const alertaB = insumoEmAlerta(b);

    if (alertaA && !alertaB) return -1;
    if (!alertaA && alertaB) return 1;

    if (ordenacao === "estoqueAsc") {
      return Number(a.estoque || 0) - Number(b.estoque || 0);
    }

    if (ordenacao === "estoqueDesc") {
      return Number(b.estoque || 0) - Number(a.estoque || 0);
    }

    if (ordenacao === "custoAsc") {
      return Number(a.custo || 0) - Number(b.custo || 0);
    }

    if (ordenacao === "custoDesc") {
      return Number(b.custo || 0) - Number(a.custo || 0);
    }

    return String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR");
  });

  return copia;
}

function prepararInsumo(insumo, alertaPadrao) {
  return {
    ...insumo,
    unidade: insumo.unidade || "un",
    nomeEdit: insumo.nome || "",
    categoriaEdit: insumo.categoria || "",
    codigoBarrasEdit: insumo.codigoBarras || "",
    limiteAlertaEdit: String(insumo.limiteAlerta ?? alertaPadrao ?? 12),
    custoEdit:
      insumo.custo !== undefined && insumo.custo !== null
        ? String(insumo.custo)
        : "",
    precoVendaEdit:
      insumo.precoVenda !== undefined && insumo.precoVenda !== null
        ? String(insumo.precoVenda)
        : "",
    unidadeEdit: insumo.unidade || "un",
    quantidadeDigitada: "",
    editando: false,
    processando: false,
  };
}

function calcularCustoReceita(receita, insumos) {
  if (!receita?.ingredientes?.length) return 0;

  return receita.ingredientes.reduce((acc, ingrediente) => {
    const insumo = insumos.find((item) => item.id === ingrediente.insumoId);
    if (!insumo) return acc;

    const custoUnitario = Number(insumo.custo || 0);
    const quantidade = Number(ingrediente.quantidade || 0);

    return acc + custoUnitario * quantidade;
  }, 0);
}

function TelaBloqueio({ diasRestantes, trialExpirado, onAbrirPagamento, onSair }) {
  const titulo = trialExpirado
    ? "Seu acesso ao Invexa Food está bloqueado"
    : "Seu teste grátis ainda está ativo";

  const descricao = trialExpirado
    ? "Seu período grátis terminou. Para continuar usando insumos, receitas, vendas e lucro, ative o plano mensal."
    : `Você ainda tem ${diasRestantes} dia(s) grátis. Se quiser, já pode ativar agora e evitar bloqueio depois.`;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #ecf4ff 0%, #dce9ff 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "980px",
          background: "#ffffff",
          borderRadius: "30px",
          border: "1px solid #dbe7ff",
          boxShadow: "0 24px 60px rgba(22, 62, 122, 0.14)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background:
              "linear-gradient(135deg, #163e7a 0%, #1d4f91 45%, #2563eb 100%)",
            padding: "28px 32px",
            color: "#ffffff",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "20px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <img
                src={logoInvexa}
                alt="Invexa"
                style={{ width: "56px", height: "56px", objectFit: "contain" }}
              />
              <div>
                <div style={{ fontSize: "30px", fontWeight: "800", lineHeight: 1.1 }}>
                  Invexa Food
                </div>
                <div style={{ marginTop: "6px", fontSize: "14px", opacity: 0.88 }}>
                  Insumos, receitas e lucro por lanche
                </div>
              </div>
            </div>

            <div
              style={{
                background: trialExpirado
                  ? "rgba(239,68,68,0.18)"
                  : "rgba(255,255,255,0.16)",
                border: trialExpirado
                  ? "1px solid rgba(254,202,202,0.45)"
                  : "1px solid rgba(255,255,255,0.22)",
                color: "#ffffff",
                borderRadius: "999px",
                padding: "10px 16px",
                fontSize: "13px",
                fontWeight: "800",
              }}
            >
              {trialExpirado
                ? "Plano necessário para continuar"
                : `Teste grátis ativo • ${diasRestantes} dia(s)`}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "32px",
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.4fr) minmax(280px, 0.9fr)",
            gap: "24px",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "8px 12px",
                borderRadius: "999px",
                background: trialExpirado ? "#fee2e2" : "#dbeafe",
                color: trialExpirado ? "#991b1b" : "#1d4ed8",
                fontSize: "12px",
                fontWeight: "800",
                marginBottom: "16px",
              }}
            >
              {trialExpirado ? "Acesso bloqueado" : "Período de avaliação"}
            </div>

            <h1
              style={{
                margin: 0,
                color: "#0f172a",
                fontSize: "34px",
                lineHeight: 1.15,
                fontWeight: "800",
              }}
            >
              {titulo}
            </h1>

            <p
              style={{
                margin: "16px 0 0 0",
                color: "#475569",
                fontSize: "16px",
                lineHeight: 1.7,
                maxWidth: "680px",
              }}
            >
              {descricao}
            </p>
          </div>

          <div
            style={{
              background: "linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)",
              border: "1px solid #dbe7ff",
              borderRadius: "24px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div>
              <div style={{ color: "#163e7a", fontSize: "22px", fontWeight: "800" }}>
                {trialExpirado ? "Reativar acesso" : "Garantir acesso contínuo"}
              </div>
              <div
                style={{
                  marginTop: "8px",
                  color: "#475569",
                  fontSize: "14px",
                  lineHeight: 1.7,
                }}
              >
                Controle completo da operação da sua hamburgueria em um só painel.
              </div>
            </div>

            <div
              style={{
                background: "#ffffff",
                border: "1px solid #dbe7ff",
                borderRadius: "18px",
                padding: "16px",
              }}
            >
              <div style={{ color: "#64748b", fontSize: "13px", marginBottom: "6px" }}>
                Plano mensal
              </div>
              <div style={{ color: "#163e7a", fontSize: "28px", fontWeight: "800" }}>
                R$ {VALOR_MENSAL.toFixed(2).replace(".", ",")}
              </div>
            </div>

            <Botao style={styles.botaoPrimario} onClick={onAbrirPagamento}>
              {trialExpirado ? "Assinar e liberar agora" : "Assinar agora"}
            </Botao>

            <Botao style={styles.botaoSecundario} onClick={onSair}>
              Sair da conta
            </Botao>
          </div>
        </div>
      </div>
    </div>
  );
}

function CardInsumo({
  insumo,
  configApp,
  alterarCampoInsumoLocal,
  alternarEdicaoInsumo,
  cancelarEdicaoInsumo,
  handleSalvarEdicaoInsumo,
  handleAdicionarAoEstoque,
  handleBaixaManual,
  handleExcluirInsumo,
}) {
  const estoque = Number(insumo.estoque || 0);
  const emAlerta = insumoEmAlerta(insumo);
  const semEstoque = estoque <= 0;

  return (
    <div
      style={{
        border: emAlerta ? "1px solid #fecaca" : "1px solid #dbe7ff",
        borderRadius: "20px",
        padding: "18px",
        background: emAlerta ? "#fff7f7" : "#ffffff",
        opacity: insumo.processando ? 0.75 : 1,
        marginBottom: "14px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong style={{ fontSize: "18px", color: "#0f172a" }}>
            {insumo.nome || "Insumo sem nome"}
          </strong>
          <div style={{ color: "#64748b", marginTop: "4px", fontSize: "14px" }}>
            {insumo.categoria || "Sem categoria"} • Unidade: {insumo.unidade || "un"}
          </div>
        </div>

        <span
          style={{
            padding: "8px 12px",
            borderRadius: "999px",
            fontWeight: "800",
            fontSize: "12px",
            background: semEstoque ? "#fee2e2" : emAlerta ? "#fef3c7" : "#dcfce7",
            color: semEstoque ? "#991b1b" : emAlerta ? "#92400e" : "#166534",
          }}
        >
          {semEstoque ? "Sem estoque" : emAlerta ? "Em alerta" : "Normal"}
        </span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: "12px",
          marginTop: "16px",
        }}
      >
        <div
          style={{
            background: "#f8fbff",
            border: "1px solid #dbe7ff",
            borderRadius: "16px",
            padding: "14px",
          }}
        >
          <div style={{ color: "#64748b", fontSize: "13px" }}>Estoque atual</div>
          <strong style={{ fontSize: "24px", color: "#163e7a" }}>
            {estoque} {insumo.unidade || "un"}
          </strong>
        </div>

        <div
          style={{
            background: "#f8fbff",
            border: "1px solid #dbe7ff",
            borderRadius: "16px",
            padding: "14px",
          }}
        >
          <div style={{ color: "#64748b", fontSize: "13px" }}>Custo</div>
          <strong style={{ fontSize: "20px", color: "#163e7a" }}>
            {formatarMoeda(insumo.custo || 0, "BRL")}
          </strong>
        </div>

        <div
          style={{
            background: "#f8fbff",
            border: "1px solid #dbe7ff",
            borderRadius: "16px",
            padding: "14px",
          }}
        >
          <div style={{ color: "#64748b", fontSize: "13px" }}>Alerta</div>
          <strong style={{ fontSize: "20px", color: "#163e7a" }}>
            {insumo.limiteAlerta || configApp.alertaPadrao || 12}
          </strong>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "180px 1fr",
          gap: "14px",
          alignItems: "end",
          marginTop: "16px",
        }}
      >
        <div>
          <label style={styles.label}>Quantidade</label>
          <input
            style={styles.input}
            value={insumo.quantidadeDigitada || ""}
            onChange={(e) =>
              alterarCampoInsumoLocal(insumo.id, "quantidadeDigitada", e.target.value)
            }
            placeholder="Ex: 10 ou 0,250"
          />
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <Botao
            style={styles.botaoPrimario}
            onClick={() => handleAdicionarAoEstoque(insumo)}
            disabled={insumo.processando}
          >
            Entrada
          </Botao>

          <Botao
            style={styles.botaoSecundario}
            onClick={() => handleBaixaManual(insumo)}
            disabled={insumo.processando}
          >
            Saída manual
          </Botao>

          <Botao
            style={styles.botaoSecundario}
            onClick={() => alternarEdicaoInsumo(insumo.id)}
            disabled={insumo.processando}
          >
            {insumo.editando ? "Fechar edição" : "Editar"}
          </Botao>

          <Botao
            style={styles.botaoPerigo}
            onClick={() => handleExcluirInsumo(insumo)}
            disabled={insumo.processando}
          >
            Excluir
          </Botao>
        </div>
      </div>

      {insumo.editando && (
        <div
          style={{
            marginTop: "16px",
            borderTop: "1px solid #e5e7eb",
            paddingTop: "16px",
            display: "grid",
            gap: "14px",
          }}
        >
          <h3 style={{ margin: 0, color: "#0f172a" }}>Editar insumo</h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "12px",
            }}
          >
            <div>
              <label style={styles.label}>Nome</label>
              <input
                style={styles.input}
                value={insumo.nomeEdit || ""}
                onChange={(e) =>
                  alterarCampoInsumoLocal(insumo.id, "nomeEdit", e.target.value)
                }
              />
            </div>

            <div>
              <label style={styles.label}>Categoria</label>
              <input
                style={styles.input}
                value={insumo.categoriaEdit || ""}
                onChange={(e) =>
                  alterarCampoInsumoLocal(insumo.id, "categoriaEdit", e.target.value)
                }
              />
            </div>

            <div>
              <label style={styles.label}>Código de barras</label>
              <input
                style={styles.input}
                value={insumo.codigoBarrasEdit || ""}
                onChange={(e) =>
                  alterarCampoInsumoLocal(
                    insumo.id,
                    "codigoBarrasEdit",
                    apenasNumeros(e.target.value)
                  )
                }
              />
            </div>

            <div>
              <label style={styles.label}>Unidade</label>
              <select
                style={styles.input}
                value={insumo.unidadeEdit || "un"}
                onChange={(e) =>
                  alterarCampoInsumoLocal(insumo.id, "unidadeEdit", e.target.value)
                }
              >
                <option value="un">un</option>
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="l">l</option>
                <option value="ml">ml</option>
                <option value="fatia">fatia</option>
              </select>
            </div>

            <div>
              <label style={styles.label}>Limite de alerta</label>
              <input
                style={styles.input}
                value={insumo.limiteAlertaEdit || ""}
                onChange={(e) =>
                  alterarCampoInsumoLocal(
                    insumo.id,
                    "limiteAlertaEdit",
                    apenasNumeros(e.target.value)
                  )
                }
              />
            </div>

            <div>
              <label style={styles.label}>Custo por unidade</label>
              <input
                style={styles.input}
                value={insumo.custoEdit || ""}
                onChange={(e) => {
                  if (decimalInputValido(e.target.value)) {
                    alterarCampoInsumoLocal(insumo.id, "custoEdit", e.target.value);
                  }
                }}
              />
            </div>

            <div>
              <label style={styles.label}>Preço de venda avulso</label>
              <input
                style={styles.input}
                value={insumo.precoVendaEdit || ""}
                onChange={(e) => {
                  if (decimalInputValido(e.target.value)) {
                    alterarCampoInsumoLocal(
                      insumo.id,
                      "precoVendaEdit",
                      e.target.value
                    );
                  }
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Botao
              style={styles.botaoPrimario}
              onClick={() => handleSalvarEdicaoInsumo(insumo)}
              disabled={insumo.processando}
            >
              Salvar alterações
            </Botao>

            <Botao
              style={styles.botaoSecundario}
              onClick={() => cancelarEdicaoInsumo(insumo)}
              disabled={insumo.processando}
            >
              Cancelar
            </Botao>
          </div>
        </div>
      )}
    </div>
  );
}

function PainelFiltros({
  busca,
  filtroCategoria,
  filtroStatus,
  ordenacao,
  categoriasDisponiveis,
  setBusca,
  setFiltroCategoria,
  setFiltroStatus,
  setOrdenacao,
  limpar,
}) {
  return (
    <div style={styles.card}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          flexWrap: "wrap",
          marginBottom: "14px",
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "#0f172a" }}>Matéria-prima</h2>
          <p style={{ margin: "6px 0 0 0", color: "#64748b" }}>
            Controle dos insumos usados nos lanches.
          </p>
        </div>

        <Botao style={styles.botaoSecundario} onClick={limpar}>
          Limpar filtros
        </Botao>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "12px",
        }}
      >
        <div>
          <label style={styles.label}>Buscar insumo</label>
          <input
            style={styles.input}
            placeholder="Nome, categoria ou código"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>

        <div>
          <label style={styles.label}>Categoria</label>
          <select
            style={styles.input}
            value={filtroCategoria}
            onChange={(e) => setFiltroCategoria(e.target.value)}
          >
            <option value="todas">Todas</option>
            {categoriasDisponiveis.map((categoria) => (
              <option key={categoria} value={categoria}>
                {categoria}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={styles.label}>Status</label>
          <select
            style={styles.input}
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="em_alerta">Em alerta</option>
            <option value="sem_estoque">Sem estoque</option>
            <option value="com_estoque">Com estoque</option>
          </select>
        </div>

        <div>
          <label style={styles.label}>Ordenar por</label>
          <select
            style={styles.input}
            value={ordenacao}
            onChange={(e) => setOrdenacao(e.target.value)}
          >
            <option value="nome">Nome</option>
            <option value="estoqueAsc">Menor estoque</option>
            <option value="estoqueDesc">Maior estoque</option>
            <option value="custoAsc">Menor custo</option>
            <option value="custoDesc">Maior custo</option>
          </select>
        </div>
      </div>
    </div>
  );
}

export default function MainApp() {
  const [usuario, setUsuario] = useState(null);
  const [carregandoAuth, setCarregandoAuth] = useState(true);
  const [carregandoDados, setCarregandoDados] = useState(false);
  const [carregandoBilling, setCarregandoBilling] = useState(false);

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [modoCadastro, setModoCadastro] = useState(false);
  const [mostrarAuth, setMostrarAuth] = useState(false);

  const [insumos, setInsumos] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [vendas, setVendas] = useState([]);
  const [receitas, setReceitas] = useState([]);
  const [toasts, setToasts] = useState([]);

  const [novoInsumo, setNovoInsumo] = useState(novoInsumoInicial);
  const [configApp, setConfigApp] = useState(configInicial);
  const [filtros, setFiltros] = useState(filtrosIniciais);

  const [novaReceita, setNovaReceita] = useState(receitaInicial);
  const [itemIngrediente, setItemIngrediente] = useState(itemIngredienteInicial);
  const [quantidadeVendaReceita, setQuantidadeVendaReceita] = useState({});

  const [filtroPeriodo, setFiltroPeriodo] = useState("mes");
  const [filtroDia, setFiltroDia] = useState("");
  const [filtroMes, setFiltroMes] = useState(new Date().toISOString().slice(0, 7));

  const [diasRestantesTrial, setDiasRestantesTrial] = useState(DIAS_GRATIS);
  const [trialExpirado, setTrialExpirado] = useState(false);
  const [acessoLiberado, setAcessoLiberado] = useState(false);
  const [assinaturaAtiva, setAssinaturaAtiva] = useState(false);
  const [bloqueioForcado, setBloqueioForcado] = useState(false);

  const adicionarToast = (tipo, mensagem) => {
    const id = Date.now() + Math.random();
    setToasts((lista) => [...lista, { id, tipo, mensagem }]);

    setTimeout(() => {
      setToasts((lista) => lista.filter((item) => item.id !== id));
    }, 3000);
  };

  const erroEhPermissao = (error) => {
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();

    return (
      code.includes("permission-denied") ||
      message.includes("missing or insufficient permissions") ||
      message.includes("insufficient permissions")
    );
  };

  const ativarBloqueioPorPermissao = () => {
    setBloqueioForcado(false);
    setCarregandoDados(false);
    adicionarToast("erro", "Erro de permissão nos dados do Firestore.");
  };

  const marcarProcessandoInsumo = (insumoId, processando) => {
    setInsumos((lista) =>
      lista.map((insumo) =>
        insumo.id === insumoId ? { ...insumo, processando } : insumo
      )
    );
  };

  const atualizarNovoInsumo = (campo, valor) => {
    setNovoInsumo((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const atualizarFiltros = (campo, valor) => {
    setFiltros((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros(filtrosIniciais);
  };

  const alterarCampoInsumoLocal = (id, campo, valor) => {
    setInsumos((lista) =>
      lista.map((insumo) =>
        insumo.id === id ? { ...insumo, [campo]: valor } : insumo
      )
    );
  };

  const alternarEdicaoInsumo = (id) => {
    setInsumos((lista) =>
      lista.map((insumo) =>
        insumo.id === id ? { ...insumo, editando: !insumo.editando } : insumo
      )
    );
  };

  const cancelarEdicaoInsumo = (insumo) => {
    setInsumos((lista) =>
      lista.map((item) =>
        item.id === insumo.id
          ? {
              ...item,
              editando: false,
              nomeEdit: item.nome || "",
              categoriaEdit: item.categoria || "",
              codigoBarrasEdit: item.codigoBarras || "",
              limiteAlertaEdit: String(item.limiteAlerta || configApp.alertaPadrao || 12),
              custoEdit:
                item.custo !== undefined && item.custo !== null
                  ? String(item.custo)
                  : "",
              precoVendaEdit:
                item.precoVenda !== undefined && item.precoVenda !== null
                  ? String(item.precoVenda)
                  : "",
              unidadeEdit: item.unidade || "un",
            }
          : item
      )
    );
  };

  useEffect(() => {
    const unsubscribe = observarAuth((user) => {
      setUsuario(user);
      setCarregandoAuth(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const verificarAtualizacao = () => {
      try {
        const versaoSalva = localStorage.getItem("invexa_app_version");

        if (!versaoSalva) {
          localStorage.setItem("invexa_app_version", VERSAO_APP);
          return;
        }

        if (versaoSalva !== VERSAO_APP) {
          localStorage.setItem("invexa_app_version", VERSAO_APP);
          window.location.reload();
        }
      } catch (error) {
        console.error("ERRO AO VERIFICAR ATUALIZAÇÃO:", error);
      }
    };

    verificarAtualizacao();
    const interval = setInterval(verificarAtualizacao, 300000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let ativo = true;

    const carregarBilling = async () => {
      if (!usuario?.uid) {
        setDiasRestantesTrial(DIAS_GRATIS);
        setTrialExpirado(false);
        setAcessoLiberado(false);
        setAssinaturaAtiva(false);
        setBloqueioForcado(false);
        return;
      }

      setCarregandoBilling(true);

      try {
        const resposta = await inicializarBilling();

        if (!ativo) return;

        const trialAtivo = Boolean(resposta?.trialAtivo);
        const diasRestantes = Number(resposta?.diasRestantesTrial ?? DIAS_GRATIS);
        const assinaturaValida = Boolean(resposta?.assinaturaValida);
        const acesso = Boolean(resposta?.appLiberado);

        setDiasRestantesTrial(Math.max(diasRestantes, 0));
        setAcessoLiberado(acesso);
        setAssinaturaAtiva(assinaturaValida);
        setTrialExpirado(!trialAtivo && !assinaturaValida);
        setBloqueioForcado(false);
      } catch (error) {
        console.error("ERRO BILLING:", error);

        if (!ativo) return;

        setDiasRestantesTrial(DIAS_GRATIS);
        setAcessoLiberado(true);
        setAssinaturaAtiva(false);
        setTrialExpirado(false);
        setBloqueioForcado(false);
        adicionarToast("erro", "Erro ao validar billing.");
      } finally {
        if (ativo) {
          setCarregandoBilling(false);
        }
      }
    };

    carregarBilling();

    return () => {
      ativo = false;
    };
  }, [usuario?.uid]);

  useEffect(() => {
    if (!usuario?.uid) {
      setInsumos([]);
      setMovimentacoes([]);
      setVendas([]);
      setReceitas([]);
      setConfigApp(configInicial);
      setCarregandoDados(false);
      setBloqueioForcado(false);
      return;
    }

    if ((trialExpirado || bloqueioForcado) && !acessoLiberado) {
      setInsumos([]);
      setMovimentacoes([]);
      setVendas([]);
      setReceitas([]);
      setCarregandoDados(false);
      return;
    }

    setCarregandoDados(true);

    let finalizouInsumos = false;
    let finalizouMovimentacoes = false;
    let finalizouVendas = false;
    let finalizouConfig = false;
    let finalizouReceitas = false;

    const verificarFim = () => {
      if (
        finalizouInsumos &&
        finalizouMovimentacoes &&
        finalizouVendas &&
        finalizouConfig &&
        finalizouReceitas
      ) {
        setCarregandoDados(false);
      }
    };

    buscarConfiguracaoApp(usuario.uid)
      .then((config) => {
        const alertaPadrao = Number(config?.alertaPadrao || 12);

        setConfigApp({
          nomeNegocio: config?.nomeNegocio || "Invexa Food",
          alertaPadrao,
          moeda: config?.moeda || "BRL",
        });

        setNovoInsumo((anterior) => ({
          ...anterior,
          limiteAlerta: String(alertaPadrao),
        }));
      })
      .catch((error) => {
        console.error("ERRO CONFIG:", error);
        if (erroEhPermissao(error)) {
          ativarBloqueioPorPermissao();
          return;
        }
        adicionarToast("erro", "Erro ao carregar configurações.");
      })
      .finally(() => {
        finalizouConfig = true;
        verificarFim();
      });

    const unsubInsumos = ouvirProdutos(
      usuario.uid,
      (lista) => {
        setInsumos((anterior) => {
          const tratada = lista.map((insumo) =>
            prepararInsumo(insumo, configApp.alertaPadrao || 12)
          );

          return tratada.map((novoItem) => {
            const antigo = anterior.find((item) => item.id === novoItem.id);
            if (!antigo) return novoItem;

            return {
              ...novoItem,
              quantidadeDigitada: antigo.quantidadeDigitada || "",
              editando: antigo.editando || false,
              processando: antigo.processando || false,
              nomeEdit: antigo.nomeEdit ?? novoItem.nomeEdit,
              categoriaEdit: antigo.categoriaEdit ?? novoItem.categoriaEdit,
              codigoBarrasEdit: antigo.codigoBarrasEdit ?? novoItem.codigoBarrasEdit,
              limiteAlertaEdit: antigo.limiteAlertaEdit ?? novoItem.limiteAlertaEdit,
              custoEdit: antigo.custoEdit ?? novoItem.custoEdit,
              precoVendaEdit: antigo.precoVendaEdit ?? novoItem.precoVendaEdit,
              unidadeEdit: antigo.unidadeEdit ?? novoItem.unidadeEdit,
            };
          });
        });

        finalizouInsumos = true;
        verificarFim();
      },
      (error) => {
        console.error("ERRO INSUMOS:", error);
        if (erroEhPermissao(error)) {
          ativarBloqueioPorPermissao();
        } else {
          adicionarToast("erro", "Erro ao carregar insumos.");
        }
        finalizouInsumos = true;
        verificarFim();
      }
    );

    const unsubMovimentacoes = ouvirMovimentacoes(
      usuario.uid,
      (lista) => {
        setMovimentacoes(lista);
        finalizouMovimentacoes = true;
        verificarFim();
      },
      (error) => {
        console.error("ERRO MOVIMENTAÇÕES:", error);
        if (erroEhPermissao(error)) {
          ativarBloqueioPorPermissao();
        } else {
          adicionarToast("erro", "Erro ao carregar movimentações.");
        }
        finalizouMovimentacoes = true;
        verificarFim();
      }
    );

    const unsubVendas = ouvirVendas(
      usuario.uid,
      (lista) => {
        setVendas(lista);
        finalizouVendas = true;
        verificarFim();
      },
      (error) => {
        console.error("ERRO VENDAS:", error);
        if (erroEhPermissao(error)) {
          ativarBloqueioPorPermissao();
        } else {
          adicionarToast("erro", "Erro ao carregar vendas.");
        }
        finalizouVendas = true;
        verificarFim();
      }
    );

    const unsubReceitas = ouvirReceitas(
      usuario.uid,
      (lista) => {
        setReceitas(lista);
        finalizouReceitas = true;
        verificarFim();
      },
      (error) => {
        console.error("ERRO RECEITAS:", error);
        if (erroEhPermissao(error)) {
          ativarBloqueioPorPermissao();
        } else {
          adicionarToast("erro", "Erro ao carregar receitas.");
        }
        finalizouReceitas = true;
        verificarFim();
      }
    );

    return () => {
      if (typeof unsubInsumos === "function") unsubInsumos();
      if (typeof unsubMovimentacoes === "function") unsubMovimentacoes();
      if (typeof unsubVendas === "function") unsubVendas();
      if (typeof unsubReceitas === "function") unsubReceitas();
    };
  }, [usuario?.uid, configApp.alertaPadrao, trialExpirado, acessoLiberado, bloqueioForcado]);

  const categoriasDisponiveis = useMemo(() => {
    const categorias = insumos
      .map((item) => String(item.categoria || "").trim())
      .filter(Boolean);

    return [...new Set(categorias)].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [insumos]);

  const movimentacoesFiltradas = useMemo(() => {
    if (filtroPeriodo === "dia" && filtroDia) {
      return movimentacoes.filter((item) => item.dia === filtroDia);
    }

    if (filtroPeriodo === "mes" && filtroMes) {
      return movimentacoes.filter((item) => item.mes === filtroMes);
    }

    return movimentacoes;
  }, [movimentacoes, filtroPeriodo, filtroDia, filtroMes]);

  const vendasFiltradas = useMemo(() => {
    if (filtroPeriodo === "dia" && filtroDia) {
      return vendas.filter((item) => item.dia === filtroDia);
    }

    if (filtroPeriodo === "mes" && filtroMes) {
      return vendas.filter((item) => item.mes === filtroMes);
    }

    return vendas;
  }, [vendas, filtroPeriodo, filtroDia, filtroMes]);

  const insumosFiltrados = useMemo(() => {
    const termo = filtros.busca.trim().toLowerCase();

    const lista = insumos.filter((insumo) => {
      const bateBusca =
        !termo ||
        String(insumo.nome || "").toLowerCase().includes(termo) ||
        String(insumo.categoria || "").toLowerCase().includes(termo) ||
        String(insumo.codigoBarras || "").toLowerCase().includes(termo);

      const bateCategoria =
        filtros.categoria === "todas" ||
        String(insumo.categoria || "") === filtros.categoria;

      const estoque = Number(insumo.estoque || 0);
      const emAlerta = insumoEmAlerta(insumo);

      let bateStatus = true;
      if (filtros.status === "em_alerta") bateStatus = emAlerta;
      if (filtros.status === "sem_estoque") bateStatus = estoque <= 0;
      if (filtros.status === "com_estoque") bateStatus = estoque > 0;

      return bateBusca && bateCategoria && bateStatus;
    });

    return ordenarInsumos(lista, filtros.ordenacao);
  }, [insumos, filtros]);

  const insumosEmAlerta = useMemo(
    () => insumos.filter((insumo) => insumoEmAlerta(insumo)),
    [insumos]
  );

  const totalInsumos = insumos.length;
  const totalItensEmEstoque = insumos.reduce(
    (acc, item) => acc + Number(item.estoque || 0),
    0
  );
  const totalValorVendido = vendasFiltradas.reduce(
    (acc, item) => acc + Number(item.valorVenda || 0),
    0
  );
  const totalLucro = vendasFiltradas.reduce(
    (acc, item) => acc + Number(item.lucroEstimado || 0),
    0
  );
  const totalEstoqueParado = insumos.reduce(
    (acc, item) => acc + Number(item.custo || 0) * Number(item.estoque || 0),
    0
  );
  const valorMedioVenda = vendasFiltradas.length
    ? totalValorVendido / vendasFiltradas.length
    : 0;

  const rankingVendas = useMemo(() => {
    const mapa = new Map();

    vendasFiltradas.forEach((item) => {
      const chave = item.receitaId || item.produtoId || item.nomeProduto;
      const atual = mapa.get(chave) || {
        chave,
        nome: item.nomeProduto || "Sem nome",
        quantidade: 0,
      };

      atual.quantidade += Number(item.quantidade || 0);
      mapa.set(chave, atual);
    });

    return [...mapa.values()].sort((a, b) => b.quantidade - a.quantidade);
  }, [vendasFiltradas]);

  const lancheMaisVendido = rankingVendas[0] || null;

  const dadosReceitas = useMemo(() => {
    return receitas.map((receita) => {
      const custo = calcularCustoReceita(receita, insumos);
      const precoVenda = Number(receita.precoVenda || 0);
      const lucro = precoVenda - custo;

      return {
        ...receita,
        custo,
        lucro,
      };
    });
  }, [receitas, insumos]);

  const handleCadastrar = async () => {
    try {
      if (!email.trim() || !senha.trim()) {
        adicionarToast("erro", "Preencha email e senha.");
        return;
      }

      await cadastrarUsuario(email.trim(), senha.trim());

      try {
        await inicializarBilling();
      } catch (billingError) {
        console.error("ERRO AO INICIALIZAR BILLING APÓS CADASTRO:", billingError);
      }

      setEmail("");
      setSenha("");
      adicionarToast("sucesso", "Conta criada com sucesso.");
    } catch (error) {
      console.error("ERRO CADASTRO:", error);

      if (error?.code === "auth/email-already-in-use") {
        adicionarToast("erro", "Esse email já está cadastrado.");
        return;
      }

      if (error?.code === "auth/invalid-email") {
        adicionarToast("erro", "Email inválido.");
        return;
      }

      if (error?.code === "auth/weak-password") {
        adicionarToast("erro", "Senha muito fraca.");
        return;
      }

      adicionarToast("erro", error?.message || "Erro ao criar conta.");
    }
  };

  const handleEntrar = async () => {
    try {
      if (!email.trim() || !senha.trim()) {
        adicionarToast("erro", "Preencha email e senha.");
        return;
      }

      await entrarUsuario(email.trim(), senha.trim());

      setEmail("");
      setSenha("");
      adicionarToast("sucesso", "Login realizado.");
    } catch (error) {
      console.error("ERRO LOGIN:", error);
      adicionarToast("erro", "Erro ao entrar.");
    }
  };

  const handleSair = async () => {
    try {
      await sairUsuario();
    } catch (error) {
      console.error("ERRO SAIR:", error);
      adicionarToast("erro", "Erro ao sair.");
    }
  };

  const handleAbrirPagamento = async () => {
    try {
      const resposta = await inicializarBilling();

      if (!resposta?.paymentLink) {
        adicionarToast("erro", "Link de pagamento não encontrado.");
        return;
      }

      window.open(resposta.paymentLink, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("ERRO ABRIR PAGAMENTO:", error);
      adicionarToast("erro", "Erro ao abrir pagamento.");
    }
  };

  const handleSalvarConfiguracao = async () => {
    try {
      if (!usuario?.uid) return;

      const alertaPadrao = parseInt(String(configApp.alertaPadrao || "12"), 10);

      if (Number.isNaN(alertaPadrao) || alertaPadrao < 0) {
        adicionarToast("erro", "Digite um alerta padrão válido.");
        return;
      }

      await salvarConfiguracaoApp(usuario.uid, {
        nomeNegocio:
          String(configApp.nomeNegocio || "Invexa Food").trim() || "Invexa Food",
        alertaPadrao,
        moeda: "BRL",
      });

      setConfigApp((anterior) => ({ ...anterior, alertaPadrao }));
      adicionarToast("sucesso", "Configurações salvas.");
    } catch (error) {
      console.error("ERRO CONFIGURAÇÃO:", error);
      adicionarToast("erro", "Erro ao salvar configurações.");
    }
  };

  const handleAdicionarInsumo = async () => {
    try {
      if (!usuario?.uid) return;

      const nome = String(novoInsumo.nome || "").trim();
      const categoria = String(novoInsumo.categoria || "").trim();
      const codigoBarras = apenasNumeros(novoInsumo.codigoBarras || "");
      const estoque = Number(String(novoInsumo.estoque || "0").replace(",", "."));
     const limiteAlerta = Number(
  String(novoInsumo.limiteAlerta || configApp.alertaPadrao || 12).replace(",", ".")
);
      const custo = paraNumero(novoInsumo.custo);
      const precoVenda = paraNumero(novoInsumo.precoVenda);
      const unidade = String(novoInsumo.unidade || "un");

      if (!nome) {
        adicionarToast("erro", "Digite o nome do insumo.");
        return;
      }

      if (Number.isNaN(estoque) || estoque < 0) {
        adicionarToast("erro", "Digite um estoque inicial válido.");
        return;
      }

      if (Number.isNaN(limiteAlerta) || limiteAlerta < 0) {
        adicionarToast("erro", "Digite um limite de alerta válido.");
        return;
      }

      await adicionarProduto(usuario.uid, {
        nome,
        categoria,
        codigoBarras,
        estoque,
        limiteAlerta,
        custo,
        precoVenda,
        unidade,
      });

      setNovoInsumo({
        ...novoInsumoInicial,
        limiteAlerta: String(configApp.alertaPadrao || 12),
      });

      adicionarToast("sucesso", "Insumo cadastrado com sucesso.");
    } catch (error) {
      console.error("ERRO ADICIONAR INSUMO:", error);
      adicionarToast("erro", "Erro ao cadastrar insumo.");
    }
  };

  const handleSalvarEdicaoInsumo = async (insumo) => {
    try {
      if (!usuario?.uid) return;

      const nome = String(insumo.nomeEdit || "").trim();
      const categoria = String(insumo.categoriaEdit || "").trim();
      const codigoBarras = apenasNumeros(insumo.codigoBarrasEdit || "");
      const limiteAlerta = parseInt(
        String(insumo.limiteAlertaEdit || configApp.alertaPadrao || 12),
        10
      );
      const custo = paraNumero(insumo.custoEdit);
      const precoVenda = paraNumero(insumo.precoVendaEdit);
      const unidade = String(insumo.unidadeEdit || "un");

      if (!nome) {
        adicionarToast("erro", "O nome do insumo é obrigatório.");
        return;
      }

      if (Number.isNaN(limiteAlerta) || limiteAlerta < 0) {
        adicionarToast("erro", "Digite um limite de alerta válido.");
        return;
      }

      marcarProcessandoInsumo(insumo.id, true);

      await atualizarProduto(usuario.uid, insumo.id, {
        nome,
        categoria,
        codigoBarras,
        limiteAlerta,
        custo,
        precoVenda,
        unidade,
      });

      setInsumos((lista) =>
        lista.map((item) =>
          item.id === insumo.id ? { ...item, editando: false } : item
        )
      );

      adicionarToast("sucesso", "Insumo atualizado com sucesso.");
    } catch (error) {
      console.error("ERRO EDIÇÃO INSUMO:", error);
      adicionarToast("erro", "Erro ao salvar edição do insumo.");
    } finally {
      marcarProcessandoInsumo(insumo.id, false);
    }
  };

  const handleAdicionarAoEstoque = async (insumo) => {
    try {
      if (!usuario?.uid) return;

      const quantidade = Number(
        String(insumo.quantidadeDigitada || "0").replace(",", ".")
      );

      if (Number.isNaN(quantidade) || quantidade <= 0) {
        adicionarToast("erro", "Digite uma quantidade válida para entrada.");
        return;
      }

      marcarProcessandoInsumo(insumo.id, true);

      const estoqueAtual = Number(insumo.estoque || 0);
      const novoEstoque = estoqueAtual + quantidade;

      await atualizarProduto(usuario.uid, insumo.id, {
        estoque: novoEstoque,
      });

      await registrarMovimentacao(usuario.uid, {
        produtoId: insumo.id,
        nomeProduto: insumo.nome,
        tipo: "entrada",
        quantidade,
        estoqueAntes: estoqueAtual,
        estoqueDepois: novoEstoque,
        observacao: "Entrada manual",
      });

      alterarCampoInsumoLocal(insumo.id, "quantidadeDigitada", "");
      adicionarToast("sucesso", `Entrada registrada em ${insumo.nome}.`);
    } catch (error) {
      console.error("ERRO ENTRADA INSUMO:", error);
      adicionarToast("erro", "Erro ao registrar entrada.");
    } finally {
      marcarProcessandoInsumo(insumo.id, false);
    }
  };

  const handleBaixaManual = async (insumo) => {
    try {
      if (!usuario?.uid) return;

      const quantidade = Number(
        String(insumo.quantidadeDigitada || "0").replace(",", ".")
      );
      const estoqueAtual = Number(insumo.estoque || 0);

      if (Number.isNaN(quantidade) || quantidade <= 0) {
        adicionarToast("erro", "Digite uma quantidade válida para saída.");
        return;
      }

      if (quantidade > estoqueAtual) {
        adicionarToast("erro", "A saída não pode ser maior que o estoque.");
        return;
      }

      marcarProcessandoInsumo(insumo.id, true);

      const novoEstoque = estoqueAtual - quantidade;

      await atualizarProduto(usuario.uid, insumo.id, {
        estoque: novoEstoque,
        ultimaSaidaEm: new Date().toISOString(),
      });

      await registrarMovimentacao(usuario.uid, {
        produtoId: insumo.id,
        nomeProduto: insumo.nome,
        tipo: "saida",
        quantidade,
        estoqueAntes: estoqueAtual,
        estoqueDepois: novoEstoque,
        observacao: "Saída manual",
      });

      alterarCampoInsumoLocal(insumo.id, "quantidadeDigitada", "");
      adicionarToast("sucesso", `Saída registrada em ${insumo.nome}.`);
    } catch (error) {
      console.error("ERRO SAÍDA INSUMO:", error);
      adicionarToast("erro", "Erro ao registrar saída.");
    } finally {
      marcarProcessandoInsumo(insumo.id, false);
    }
  };

  const handleExcluirInsumo = async (insumo) => {
    try {
      if (!usuario?.uid) return;

      const confirmar = window.confirm(`Deseja excluir o insumo "${insumo.nome}"?`);
      if (!confirmar) return;

      marcarProcessandoInsumo(insumo.id, true);
      await excluirProduto(usuario.uid, insumo.id);

      adicionarToast("sucesso", "Insumo excluído com sucesso.");
    } catch (error) {
      console.error("ERRO EXCLUIR INSUMO:", error);
      adicionarToast("erro", "Erro ao excluir insumo.");
    } finally {
      marcarProcessandoInsumo(insumo.id, false);
    }
  };

  const adicionarIngredienteNaReceita = () => {
    const insumoId = String(itemIngrediente.insumoId || "");
    const quantidade = Number(
      String(itemIngrediente.quantidade || "0").replace(",", ".")
    );

    if (!insumoId) {
      adicionarToast("erro", "Selecione um insumo.");
      return;
    }

    if (Number.isNaN(quantidade) || quantidade <= 0) {
      adicionarToast("erro", "Digite uma quantidade válida.");
      return;
    }

    const jaExiste = novaReceita.ingredientes.some(
      (item) => item.insumoId === insumoId
    );

    if (jaExiste) {
      adicionarToast("erro", "Esse insumo já foi adicionado na receita.");
      return;
    }

    setNovaReceita((anterior) => ({
      ...anterior,
      ingredientes: [...anterior.ingredientes, { insumoId, quantidade }],
    }));

    setItemIngrediente(itemIngredienteInicial);
  };

  const removerIngredienteDaReceita = (insumoId) => {
    setNovaReceita((anterior) => ({
      ...anterior,
      ingredientes: anterior.ingredientes.filter(
        (item) => item.insumoId !== insumoId
      ),
    }));
  };

  const handleSalvarReceita = async () => {
    try {
      if (!usuario?.uid) return;

      const nome = String(novaReceita.nome || "").trim();
      const precoVenda = paraNumero(novaReceita.precoVenda);

      if (!nome) {
        adicionarToast("erro", "Digite o nome do lanche.");
        return;
      }

      if (!novaReceita.ingredientes.length) {
        adicionarToast("erro", "Adicione pelo menos um ingrediente.");
        return;
      }

      if (Number(precoVenda) <= 0) {
        adicionarToast("erro", "Digite um preço de venda válido.");
        return;
      }

      await adicionarReceita(usuario.uid, {
        nome,
        precoVenda,
        ingredientes: novaReceita.ingredientes,
      });

      setNovaReceita(receitaInicial);
      setItemIngrediente(itemIngredienteInicial);
      adicionarToast("sucesso", "Receita salva com sucesso.");
    } catch (error) {
      console.error("ERRO SALVAR RECEITA:", error);
      adicionarToast("erro", "Erro ao salvar receita.");
    }
  };

  const handleExcluirReceita = async (receitaId) => {
    try {
      if (!usuario?.uid) return;

      const confirmar = window.confirm("Deseja excluir essa receita?");
      if (!confirmar) return;

      await excluirReceita(usuario.uid, receitaId);
      adicionarToast("sucesso", "Receita excluída.");
    } catch (error) {
      console.error("ERRO EXCLUIR RECEITA:", error);
      adicionarToast("erro", "Erro ao excluir receita.");
    }
  };

  const handleVenderReceita = async (receita) => {
    try {
      if (!usuario?.uid) return;

      const quantidadeVenda = Number(
        String(quantidadeVendaReceita[receita.id] || "1").replace(",", ".")
      );

      if (Number.isNaN(quantidadeVenda) || quantidadeVenda <= 0) {
        adicionarToast("erro", "Digite uma quantidade válida para venda.");
        return;
      }

      const faltas = [];
      let custoTotal = 0;

      for (const ingrediente of receita.ingredientes) {
        const insumo = insumos.find((item) => item.id === ingrediente.insumoId);

        if (!insumo) {
          adicionarToast("erro", "Um insumo da receita não foi encontrado.");
          return;
        }

        const consumo = Number(ingrediente.quantidade || 0) * quantidadeVenda;
        const estoqueAtual = Number(insumo.estoque || 0);

        if (consumo > estoqueAtual) {
          faltas.push(`${insumo.nome} sem estoque suficiente`);
        }

        custoTotal += Number(insumo.custo || 0) * consumo;
      }

      if (faltas.length) {
        adicionarToast("erro", faltas[0]);
        return;
      }

      for (const ingrediente of receita.ingredientes) {
        const insumo = insumos.find((item) => item.id === ingrediente.insumoId);
        const consumo = Number(ingrediente.quantidade || 0) * quantidadeVenda;
        const estoqueAtual = Number(insumo.estoque || 0);
        const novoEstoque = estoqueAtual - consumo;

        await atualizarProduto(usuario.uid, insumo.id, {
          estoque: novoEstoque,
          ultimaSaidaEm: new Date().toISOString(),
        });

        await registrarMovimentacao(usuario.uid, {
          produtoId: insumo.id,
          nomeProduto: insumo.nome,
          tipo: "saida",
          quantidade: consumo,
          estoqueAntes: estoqueAtual,
          estoqueDepois: novoEstoque,
          origem: "venda_receita",
          receitaId: receita.id,
          receitaNome: receita.nome,
          observacao: `Baixa automática por venda de ${receita.nome}`,
        });
      }

      const valorVenda = Number(receita.precoVenda || 0) * quantidadeVenda;
      const lucroEstimado = valorVenda - custoTotal;

      await registrarVenda(usuario.uid, {
        produtoId: receita.id,
        receitaId: receita.id,
        nomeProduto: receita.nome,
        quantidade: quantidadeVenda,
        valorUnitario: Number(receita.precoVenda || 0),
        valorVenda,
        lucroEstimado,
        custoTotal,
        origem: "receita",
        observacao: `Venda de ${receita.nome}`,
      });

      setQuantidadeVendaReceita((anterior) => ({
        ...anterior,
        [receita.id]: "",
      }));

      adicionarToast("sucesso", `Venda registrada: ${receita.nome}.`);
    } catch (error) {
      console.error("ERRO AO VENDER RECEITA:", error);
      adicionarToast("erro", "Erro ao registrar venda do lanche.");
    }
  };

  const exportarRelatorioPDF = () => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(18);
      doc.text(`Relatório - ${configApp.nomeNegocio || "Invexa Food"}`, 14, 18);
      doc.setFontSize(11);
      doc.text(`Período: ${filtroPeriodo}`, 14, 26);
      doc.text(`Gerado em: ${new Date().toLocaleString("pt-BR")}`, 14, 32);

      autoTable(doc, {
        startY: 40,
        head: [["Resumo", "Valor"]],
        body: [
          ["Insumos cadastrados", String(totalInsumos)],
          ["Itens em estoque", String(totalItensEmEstoque)],
          ["Insumos em alerta", String(insumosEmAlerta.length)],
          ["Receitas cadastradas", String(receitas.length)],
          ["Valor vendido", formatarMoeda(totalValorVendido, "BRL")],
          ["Lucro estimado", formatarMoeda(totalLucro, "BRL")],
          ["Estoque parado", formatarMoeda(totalEstoqueParado, "BRL")],
        ],
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [["Insumo", "Estoque", "Unidade", "Custo"]],
        body: insumos.map((item) => [
          item.nome || "-",
          String(item.estoque || 0),
          item.unidade || "un",
          formatarMoeda(item.custo || 0, "BRL"),
        ]),
      });

      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [["Lanche", "Preço", "Custo", "Lucro"]],
        body: dadosReceitas.map((item) => [
          item.nome || "-",
          formatarMoeda(item.precoVenda || 0, "BRL"),
          formatarMoeda(item.custo || 0, "BRL"),
          formatarMoeda(item.lucro || 0, "BRL"),
        ]),
      });

      doc.save(
        `relatorio-${(configApp.nomeNegocio || "invexa-food")
          .toLowerCase()
          .replace(/\s+/g, "-")}.pdf`
      );

      adicionarToast("sucesso", "PDF exportado com sucesso.");
    } catch (error) {
      console.error("ERRO PDF:", error);
      adicionarToast("erro", "Erro ao exportar PDF.");
    }
  };

  if (carregandoAuth || carregandoDados || carregandoBilling) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fbff",
          padding: "24px",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            padding: "22px 28px",
            borderRadius: "18px",
            border: "1px solid #dbe7ff",
            boxShadow: "0 20px 45px rgba(15,23,42,0.08)",
            color: "#163e7a",
            fontWeight: "800",
          }}
        >
          Carregando Invexa Food...
        </div>
      </div>
    );
  }

  if (!usuario && !mostrarAuth) {
    return (
      <TelaInicial
        onEntrar={() => {
          setModoCadastro(false);
          setMostrarAuth(true);
        }}
        onComecarGratis={() => {
          setModoCadastro(true);
          setMostrarAuth(true);
        }}
      />
    );
  }

  if (!usuario) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(180deg, #eef5ff 0%, #dbeafe 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
      >
        <Toasts toasts={toasts} />

        <div
          style={{
            width: "100%",
            maxWidth: "420px",
            background: "#ffffff",
            borderRadius: "28px",
            border: "1px solid #dbe7ff",
            boxShadow: "0 24px 60px rgba(15,23,42,0.10)",
            padding: "28px",
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <img
              src={logoInvexa}
              alt="Invexa"
              style={{ width: "72px", height: "72px", objectFit: "contain" }}
            />
            <h1 style={{ margin: "12px 0 6px 0", color: "#0f172a" }}>Invexa Food</h1>
            <p style={{ margin: 0, color: "#64748b" }}>
              Controle de insumos e lucro por lanche
            </p>
          </div>

          <div style={{ display: "grid", gap: "14px" }}>
            <div>
              <label style={styles.label}>Email</label>
              <input
                style={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Digite seu email"
              />
            </div>

            <div>
              <label style={styles.label}>Senha</label>
              <input
                type="password"
                style={styles.input}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="Digite sua senha"
              />
            </div>

            {modoCadastro ? (
              <Botao style={styles.botaoPrimario} onClick={handleCadastrar}>
                Criar conta
              </Botao>
            ) : (
              <Botao style={styles.botaoPrimario} onClick={handleEntrar}>
                Entrar
              </Botao>
            )}

            <Botao
              style={styles.botaoSecundario}
              onClick={() => setModoCadastro((v) => !v)}
            >
              {modoCadastro ? "Já tenho conta" : "Criar conta"}
            </Botao>

            <Botao
              style={styles.botaoSecundario}
              onClick={() => setMostrarAuth(false)}
            >
              Voltar
            </Botao>
          </div>
        </div>
      </div>
    );
  }

  if ((trialExpirado || bloqueioForcado) && !acessoLiberado) {
    return (
      <TelaBloqueio
        diasRestantes={diasRestantesTrial}
        trialExpirado={true}
        onAbrirPagamento={handleAbrirPagamento}
        onSair={handleSair}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fbff",
        padding: "20px",
      }}
    >
      <Toasts toasts={toasts} />

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          display: "grid",
          gap: "20px",
        }}
      >
        <div
          style={{
            background: "#ffffff",
            borderRadius: "24px",
            border: "1px solid #dbe7ff",
            boxShadow: "0 20px 45px rgba(15,23,42,0.06)",
            padding: "20px",
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
            <img
              src={logoInvexa}
              alt="Invexa"
              style={{ width: "58px", height: "58px", objectFit: "contain" }}
            />
            <div>
              <h1 style={{ margin: 0, color: "#0f172a", fontSize: "28px" }}>
                {configApp.nomeNegocio || "Invexa Food"}
              </h1>
              <p style={{ margin: "6px 0 0 0", color: "#64748b" }}>
                Versão {VERSAO_APP} • Insumos, receitas e vendas
              </p>
              <p
                style={{
                  margin: "4px 0 0 0",
                  color: "#2563eb",
                  fontSize: "13px",
                  fontWeight: "700",
                }}
              >
                {assinaturaAtiva
                  ? "Assinatura ativa"
                  : trialExpirado
                  ? "Acesso bloqueado"
                  : `Teste grátis: ${diasRestantesTrial} dia(s) restante(s)`}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {!assinaturaAtiva && (
              <Botao style={styles.botaoPrimario} onClick={handleAbrirPagamento}>
                Assinar agora
              </Botao>
            )}

            <Botao style={styles.botaoSecundario} onClick={exportarRelatorioPDF}>
              Exportar PDF
            </Botao>

            <Botao style={styles.botaoPerigo} onClick={handleSair}>
              Sair
            </Botao>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
          }}
        >
          {[
            { titulo: "Insumos cadastrados", valor: totalInsumos },
            { titulo: "Itens em estoque", valor: totalItensEmEstoque },
            { titulo: "Insumos em alerta", valor: insumosEmAlerta.length },
            { titulo: "Valor vendido", valor: formatarMoeda(totalValorVendido, "BRL") },
          ].map((item) => (
            <div
              key={item.titulo}
              style={{
                background: "#ffffff",
                borderRadius: "20px",
                border: "1px solid #dbe7ff",
                padding: "18px",
              }}
            >
              <div style={{ color: "#64748b", fontSize: "13px" }}>{item.titulo}</div>
              <strong style={{ display: "block", color: "#163e7a", fontSize: "28px" }}>
                {item.valor}
              </strong>
            </div>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              border: "1px solid #dbe7ff",
              padding: "18px",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "13px" }}>Lanche mais vendido</div>
            <strong style={{ display: "block", color: "#163e7a", fontSize: "22px" }}>
              {lancheMaisVendido ? lancheMaisVendido.nome : "Sem dados"}
            </strong>
            <div style={{ color: "#64748b", marginTop: "6px", fontSize: "13px" }}>
              {lancheMaisVendido
                ? `${lancheMaisVendido.quantidade} unidade(s) no período`
                : "Ainda sem vendas registradas"}
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              border: "1px solid #dbe7ff",
              padding: "18px",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "13px" }}>Estoque parado</div>
            <strong style={{ display: "block", color: "#163e7a", fontSize: "22px" }}>
              {formatarMoeda(totalEstoqueParado, "BRL")}
            </strong>
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              border: "1px solid #dbe7ff",
              padding: "18px",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "13px" }}>Lucro estimado</div>
            <strong style={{ display: "block", color: "#163e7a", fontSize: "22px" }}>
              {formatarMoeda(totalLucro, "BRL")}
            </strong>
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: "20px",
              border: "1px solid #dbe7ff",
              padding: "18px",
            }}
          >
            <div style={{ color: "#64748b", fontSize: "13px" }}>Ticket médio estimado</div>
            <strong style={{ display: "block", color: "#163e7a", fontSize: "22px" }}>
              {formatarMoeda(valorMedioVenda, "BRL")}
            </strong>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.35fr 0.95fr",
            gap: "20px",
            alignItems: "start",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            <PainelFiltros
              busca={filtros.busca}
              filtroCategoria={filtros.categoria}
              filtroStatus={filtros.status}
              ordenacao={filtros.ordenacao}
              categoriasDisponiveis={categoriasDisponiveis}
              setBusca={(valor) => atualizarFiltros("busca", valor)}
              setFiltroCategoria={(valor) => atualizarFiltros("categoria", valor)}
              setFiltroStatus={(valor) => atualizarFiltros("status", valor)}
              setOrdenacao={(valor) => atualizarFiltros("ordenacao", valor)}
              limpar={limparFiltros}
            />

            <div style={styles.card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "14px",
                }}
              >
                <div>
                  <h2 style={{ margin: 0, color: "#0f172a" }}>Lista de insumos</h2>
                  <p style={{ margin: "6px 0 0 0", color: "#64748b" }}>
                    Controle da matéria-prima da operação.
                  </p>
                </div>

                <div style={{ color: "#64748b", fontSize: "14px", fontWeight: 700 }}>
                  {insumosFiltrados.length} resultado(s)
                </div>
              </div>

              {insumosFiltrados.length === 0 ? (
                <div style={{ color: "#64748b" }}>
                  Nenhum insumo encontrado com os filtros atuais.
                </div>
              ) : (
                insumosFiltrados.map((insumo) => (
                  <CardInsumo
                    key={insumo.id}
                    insumo={insumo}
                    configApp={configApp}
                    alterarCampoInsumoLocal={alterarCampoInsumoLocal}
                    alternarEdicaoInsumo={alternarEdicaoInsumo}
                    cancelarEdicaoInsumo={cancelarEdicaoInsumo}
                    handleSalvarEdicaoInsumo={handleSalvarEdicaoInsumo}
                    handleAdicionarAoEstoque={handleAdicionarAoEstoque}
                    handleBaixaManual={handleBaixaManual}
                    handleExcluirInsumo={handleExcluirInsumo}
                  />
                ))
              )}
            </div>

            <div style={styles.card}>
              <div style={{ marginBottom: "14px" }}>
                <h2 style={{ margin: 0, color: "#0f172a" }}>Receitas dos lanches</h2>
                <p style={{ margin: "6px 0 0 0", color: "#64748b" }}>
                  Monte a ficha técnica e registre a venda com baixa automática.
                </p>
              </div>

              <div style={{ display: "grid", gap: "14px", marginBottom: "18px" }}>
                <div>
                  <label style={styles.label}>Nome do lanche</label>
                  <input
                    style={styles.input}
                    value={novaReceita.nome}
                    onChange={(e) =>
                      setNovaReceita((anterior) => ({
                        ...anterior,
                        nome: e.target.value,
                      }))
                    }
                    placeholder="Ex: Smash Bacon"
                  />
                </div>

                <div>
                  <label style={styles.label}>Preço de venda</label>
                  <input
                    style={styles.input}
                    value={novaReceita.precoVenda}
                    onChange={(e) => {
                      if (decimalInputValido(e.target.value)) {
                        setNovaReceita((anterior) => ({
                          ...anterior,
                          precoVenda: e.target.value,
                        }));
                      }
                    }}
                    placeholder="Ex: 24,90"
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 180px auto",
                    gap: "10px",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <label style={styles.label}>Ingrediente</label>
                    <select
                      style={styles.input}
                      value={itemIngrediente.insumoId}
                      onChange={(e) =>
                        setItemIngrediente((anterior) => ({
                          ...anterior,
                          insumoId: e.target.value,
                        }))
                      }
                    >
                      <option value="">Selecione</option>
                      {insumos.map((insumo) => (
                        <option key={insumo.id} value={insumo.id}>
                          {insumo.nome}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={styles.label}>Quantidade</label>
                    <input
                      style={styles.input}
                      value={itemIngrediente.quantidade}
                      onChange={(e) =>
                        setItemIngrediente((anterior) => ({
                          ...anterior,
                          quantidade: e.target.value,
                        }))
                      }
                      placeholder="Ex: 2 ou 0,150"
                    />
                  </div>

                  <Botao style={styles.botaoPrimario} onClick={adicionarIngredienteNaReceita}>
                    Adicionar
                  </Botao>
                </div>

                {novaReceita.ingredientes.length > 0 && (
                  <div style={{ display: "grid", gap: "8px" }}>
                    {novaReceita.ingredientes.map((ingrediente, index) => {
                      const insumo = insumos.find((item) => item.id === ingrediente.insumoId);

                      return (
                        <div
                          key={`${ingrediente.insumoId}-${index}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            flexWrap: "wrap",
                            alignItems: "center",
                            background: "#f8fbff",
                            border: "1px solid #dbe7ff",
                            borderRadius: "14px",
                            padding: "10px 12px",
                          }}
                        >
                          <div style={{ color: "#334155" }}>
                            {insumo?.nome || "Insumo"} • {ingrediente.quantidade}{" "}
                            {insumo?.unidade || "un"}
                          </div>

                          <Botao
                            style={styles.botaoPerigo}
                            onClick={() =>
                              removerIngredienteDaReceita(ingrediente.insumoId)
                            }
                          >
                            Remover
                          </Botao>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Botao style={styles.botaoPrimario} onClick={handleSalvarReceita}>
                  Salvar receita
                </Botao>
              </div>

              <div style={{ display: "grid", gap: "14px" }}>
                {dadosReceitas.length === 0 ? (
                  <div style={{ color: "#64748b" }}>Nenhuma receita cadastrada ainda.</div>
                ) : (
                  dadosReceitas.map((receita) => (
                    <div
                      key={receita.id}
                      style={{
                        border: "1px solid #dbe7ff",
                        borderRadius: "18px",
                        padding: "16px",
                        background: "#f8fbff",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: "12px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <strong style={{ fontSize: "18px", color: "#0f172a" }}>
                            {receita.nome}
                          </strong>
                          <div style={{ color: "#64748b", marginTop: "6px", fontSize: "14px" }}>
                            Preço: {formatarMoeda(receita.precoVenda || 0, "BRL")}
                            {` • Custo: ${formatarMoeda(receita.custo || 0, "BRL")}`}
                            {` • Lucro: ${formatarMoeda(receita.lucro || 0, "BRL")}`}
                          </div>
                        </div>

                        <Botao
                          style={styles.botaoPerigo}
                          onClick={() => handleExcluirReceita(receita.id)}
                        >
                          Excluir
                        </Botao>
                      </div>

                      <div style={{ marginTop: "14px", display: "grid", gap: "8px" }}>
                        {receita.ingredientes.map((item, index) => {
                          const insumo = insumos.find((p) => p.id === item.insumoId);

                          return (
                            <div
                              key={`${receita.id}-${index}`}
                              style={{
                                background: "#ffffff",
                                borderRadius: "12px",
                                border: "1px solid #e2e8f0",
                                padding: "10px 12px",
                                fontSize: "14px",
                                color: "#334155",
                              }}
                            >
                              {insumo?.nome || "Insumo removido"} • {item.quantidade}{" "}
                              {insumo?.unidade || "un"}
                            </div>
                          );
                        })}
                      </div>

                      <div
                        style={{
                          marginTop: "16px",
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <input
                          style={styleMerge(styles.input, { maxWidth: "160px" })}
                          placeholder="Qtd vendida"
                          value={quantidadeVendaReceita[receita.id] || ""}
                          onChange={(e) =>
                            setQuantidadeVendaReceita((anterior) => ({
                              ...anterior,
                              [receita.id]: e.target.value,
                            }))
                          }
                        />

                        <Botao
                          style={styles.botaoPrimario}
                          onClick={() => handleVenderReceita(receita)}
                        >
                          Registrar venda
                        </Botao>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: "20px" }}>
<div style={styles.card}>
  <div style={{ marginBottom: "18px" }}>
    <h2 style={{ margin: 0, color: "#0f172a" }}>Cadastrar insumo</h2>
    <p style={{ margin: "6px 0 0 0", color: "#64748b" }}>
      Cadastre a matéria-prima com unidade de medida correta para controlar estoque e custo.
    </p>
  </div>

  <div style={{ display: "grid", gap: "18px" }}>
    <div
      style={{
        background: "#f8fbff",
        border: "1px solid #dbe7ff",
        borderRadius: "16px",
        padding: "16px",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: "800",
          color: "#163e7a",
          marginBottom: "12px",
        }}
      >
        Informações do insumo
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "12px",
        }}
      >
        <div>
          <label style={styles.label}>Nome</label>
          <input
            style={styles.input}
            value={novoInsumo.nome}
            onChange={(e) => atualizarNovoInsumo("nome", e.target.value)}
            placeholder="Ex: Carne 80/20"
          />
        </div>

        <div>
          <label style={styles.label}>Categoria</label>
          <input
            style={styles.input}
            value={novoInsumo.categoria}
            onChange={(e) => atualizarNovoInsumo("categoria", e.target.value)}
            placeholder="Ex: Carnes"
          />
        </div>

        <div>
          <label style={styles.label}>Código de barras</label>
          <input
            style={styles.input}
            value={novoInsumo.codigoBarras}
            onChange={(e) =>
              atualizarNovoInsumo("codigoBarras", apenasNumeros(e.target.value))
            }
            placeholder="Opcional"
          />
        </div>
      </div>
    </div>

    <div
      style={{
        background: "#f8fbff",
        border: "1px solid #dbe7ff",
        borderRadius: "16px",
        padding: "16px",
      }}
    >
      <div
        style={{
          fontSize: "14px",
          fontWeight: "800",
          color: "#163e7a",
          marginBottom: "12px",
        }}
      >
        Controle de estoque e custo
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "12px",
        }}
      >
        <div>
          <label style={styles.label}>Estoque inicial</label>
          <input
            style={styles.input}
            value={novoInsumo.estoque}
            onChange={(e) => {
              if (decimalInputValido(e.target.value)) {
                atualizarNovoInsumo("estoque", e.target.value);
              }
            }}
            placeholder="Ex: 5"
          />
        </div>

        <div>
          <label style={styles.label}>Unidade de medida</label>
          <select
            style={styles.input}
            value={novoInsumo.unidade}
            onChange={(e) => atualizarNovoInsumo("unidade", e.target.value)}
          >
            <option value="un">Unidade (un)</option>
            <option value="kg">Quilo (kg)</option>
            <option value="g">Grama (g)</option>
            <option value="l">Litro (l)</option>
            <option value="ml">Mililitro (ml)</option>
            <option value="fatia">Fatia</option>
          </select>
        </div>

        <div>
          <label style={styles.label}>
            Limite de alerta ({novoInsumo.unidade || "un"})
          </label>
          <input
            style={styles.input}
            value={novoInsumo.limiteAlerta}
            onChange={(e) => {
              if (decimalInputValido(e.target.value)) {
                atualizarNovoInsumo("limiteAlerta", e.target.value);
              }
            }}
            placeholder="Ex: 1"
          />
        </div>

        <div>
          <label style={styles.label}>
            Custo por {novoInsumo.unidade || "un"}
          </label>
          <input
            style={styles.input}
            value={novoInsumo.custo}
            onChange={(e) => {
              if (decimalInputValido(e.target.value)) {
                atualizarNovoInsumo("custo", e.target.value);
              }
            }}
            placeholder="Ex: 2,50"
          />
        </div>

        <div>
          <label style={styles.label}>Preço de venda avulso</label>
          <input
            style={styles.input}
            value={novoInsumo.precoVenda}
            onChange={(e) => {
              if (decimalInputValido(e.target.value)) {
                atualizarNovoInsumo("precoVenda", e.target.value);
              }
            }}
            placeholder="Ex: 5,00"
          />
        </div>
      </div>
    </div>

    <div>
      <Botao style={styles.botaoPrimario} onClick={handleAdicionarInsumo}>
        Cadastrar insumo
      </Botao>
    </div>
  </div>
</div>

              <div style={{ display: "grid", gap: "12px" }}>
                <div>
                  <label style={styles.label}>Nome do negócio</label>
                  <input
                    style={styles.input}
                    value={configApp.nomeNegocio}
                    onChange={(e) =>
                      setConfigApp((anterior) => ({
                        ...anterior,
                        nomeNegocio: e.target.value,
                      }))
                    }
                  />
                </div>

                <div>
                  <label style={styles.label}>Alerta padrão</label>
                  <input
                    style={styles.input}
                    value={configApp.alertaPadrao}
                    onChange={(e) =>
                      setConfigApp((anterior) => ({
                        ...anterior,
                        alertaPadrao: e.target.value,
                      }))
                    }
                  />
                </div>

                <Botao style={styles.botaoPrimario} onClick={handleSalvarConfiguracao}>
                  Salvar configurações
                </Botao>
              </div>
            </div>

            <div style={styles.card}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: "14px",
                }}
              >
                <div>
                  <h2 style={{ margin: 0, color: "#0f172a" }}>Filtros de período</h2>
                  <p style={{ margin: "6px 0 0 0", color: "#64748b" }}>
                    Histórico de vendas e movimentações.
                  </p>
                </div>
              </div>

              <div style={{ display: "grid", gap: "12px" }}>
                <div>
                  <label style={styles.label}>Período</label>
                  <select
                    style={styles.input}
                    value={filtroPeriodo}
                    onChange={(e) => setFiltroPeriodo(e.target.value)}
                  >
                    <option value="mes">Mês</option>
                    <option value="dia">Dia</option>
                    <option value="todos">Todos</option>
                  </select>
                </div>

                {filtroPeriodo === "dia" && (
                  <div>
                    <label style={styles.label}>Dia</label>
                    <input
                      type="date"
                      style={styles.input}
                      value={filtroDia}
                      onChange={(e) => setFiltroDia(e.target.value)}
                    />
                  </div>
                )}

                {filtroPeriodo === "mes" && (
                  <div>
                    <label style={styles.label}>Mês</label>
                    <input
                      type="month"
                      style={styles.input}
                      value={filtroMes}
                      onChange={(e) => setFiltroMes(e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div style={styles.card}>
              <div style={{ marginBottom: "14px" }}>
                <h2 style={{ margin: 0, color: "#0f172a" }}>Últimas vendas</h2>
              </div>

              <div style={{ display: "grid", gap: "10px" }}>
                {vendasFiltradas.length === 0 ? (
                  <div style={{ color: "#64748b" }}>Nenhuma venda registrada.</div>
                ) : (
                  vendasFiltradas
                    .slice()
                    .reverse()
                    .slice(0, 8)
                    .map((item, index) => (
                      <div
                        key={`${item.id || item.nomeProduto}-${index}`}
                        style={{
                          background: "#f8fbff",
                          border: "1px solid #dbe7ff",
                          borderRadius: "14px",
                          padding: "12px",
                        }}
                      >
                        <div style={{ fontWeight: "700", color: "#0f172a" }}>
                          {item.nomeProduto || "Venda"}
                        </div>
                        <div style={{ color: "#64748b", fontSize: "13px", marginTop: "6px" }}>
                          Qtd: {item.quantidade || 0}
                          {` • Venda: ${formatarMoeda(item.valorVenda || 0, "BRL")}`}
                          {` • Lucro: ${formatarMoeda(item.lucroEstimado || 0, "BRL")}`}
                        </div>
                        <div style={{ color: "#94a3b8", fontSize: "12px", marginTop: "6px" }}>
                          {formatarDataHora(item.criadoEm || item.createdAt || item.data)}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

            <div style={styles.card}>
              <div style={{ marginBottom: "14px" }}>
                <h2 style={{ margin: 0, color: "#0f172a" }}>Últimas movimentações</h2>
              </div>

              <div style={{ display: "grid", gap: "10px" }}>
                {movimentacoesFiltradas.length === 0 ? (
                  <div style={{ color: "#64748b" }}>Nenhuma movimentação registrada.</div>
                ) : (
                  movimentacoesFiltradas
                    .slice()
                    .reverse()
                    .slice(0, 8)
                    .map((item, index) => (
                      <div
                        key={`${item.id || item.nomeProduto}-${index}`}
                        style={{
                          background: "#f8fbff",
                          border: "1px solid #dbe7ff",
                          borderRadius: "14px",
                          padding: "12px",
                        }}
                      >
                        <div style={{ fontWeight: "700", color: "#0f172a" }}>
                          {item.nomeProduto || "Movimentação"}
                        </div>
                        <div style={{ color: "#64748b", fontSize: "13px", marginTop: "6px" }}>
                          Tipo: {item.tipo || "-"}
                          {` • Qtd: ${item.quantidade || 0}`}
                          {` • Antes: ${item.estoqueAntes ?? "-"}`}
                          {` • Depois: ${item.estoqueDepois ?? "-"}`}
                        </div>
                        <div style={{ color: "#94a3b8", fontSize: "12px", marginTop: "6px" }}>
                          {formatarDataHora(item.criadoEm || item.createdAt || item.data)}
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}